import React, { useEffect, useMemo, useRef, useState } from "react";
import { KeyboardBackspace } from "@mui/icons-material";
import { Switch, notification } from "antd";
import { useDispatch } from "react-redux";
import { CircularProgress, LinearProgress } from "@mui/material";
import JobSwmsFormV3Shell from "./JobSwmsFormV3Shell.jsx";
import JobSwmsV3PreStartContainer from "./pre-start/JobSwmsV3PreStartContainer.jsx";
import JobSwmsV3EmergencyPlansContainer from "./emergency-plans/JobSwmsV3EmergencyPlansContainer.jsx";
import { normalizeEmergencyPlans } from "./emergency-plans/emergencyPlanAdapter";
import JobSwmsV3EmergencyProceduresContainer from "./emergency-procedures/JobSwmsV3EmergencyProceduresContainer.jsx";
import { normalizeEmergencyProcedures } from "./emergency-procedures/emergencyProcedureAdapter";
import JobSwmsV3RescueReadinessContainer from "./rescue-readiness/JobSwmsV3RescueReadinessContainer.jsx";
import JobSwmsV3AddEditRescueReadinessQuestionModal from "./rescue-readiness/JobSwmsV3AddEditRescueReadinessQuestionModal.jsx";
import PreStartToggleConfirmModal from "./pre-start/PreStartToggleConfirmModal.jsx";
import EmergencyPlansToggleConfirmModal from "./emergency-plans/EmergencyPlansToggleConfirmModal.jsx";
import JobSwmsV3AddEditPreStartQuestionModal from "./pre-start/JobSwmsV3AddEditPreStartQuestionModal.jsx";
import usePreStartQuestionDraft from "./pre-start/hooks/usePreStartQuestionDraft";
import usePreStartQuestionModalState from "./pre-start/hooks/usePreStartQuestionModalState";
import usePreStartChecklistApi from "./pre-start/usePreStartChecklistApi";
import {
  normalizePreStartChecklist,
  resolveMethodOptions,
  // resolvePreStartHeadingContent
} from "./pre-start/utils/preStartChecklistV3.utils";
import preStartStyles from "./pre-start/JobSwmsFormV3PreStart.module.css";
import { serializeInitialValuesV3 } from "./SwmsFormV3Serializer";
import commonStyles from "../../../JobStyle.module.css";
import { getEmergencyPlanOptions, mapEmergencyReadinessQuestions, normalizeEmergencyServerState } from "./emergencyServerStateUtils";
import { saveJobSwmsEmergencyReadiness } from "../../../../../../actions/v2/JobSwmsEmergencyReadinessActions";
import { saveJobSwmsEmergencyPlans } from "../../../../../../actions/v2/JobSwmsEmergencyPlansActions";
import { saveJobSwmsEmergencyProcedures } from "../../../../../../actions/v2/JobSwmsEmergencyProceduresActions";
import { Strings } from "../../../../../../dataProvider/localize";
import JobSwmsFormV3SaveBar from "./JobSwmsFormV3SaveBar";
import VersionApprovalHistoryModal from "./VersionApprovalHistoryModal";
import EditHistoryModal from "./EditHistoryModal";
import { createSwmsEditContext } from "./editHistory/swmsEditContext";

const EMPTY_SWMS_DATA = {};

export default function JobSwmsFormV3Container({ onClose, onPreview, swms, jobId }) {
  const TAB_SWMS = "swms";
  const TAB_PRE_START = "prestart";
  const TAB_EMERGENCY_PLANS = "emergency-plans";
  const TAB_EMERGENCY_PROCEDURES = "emergency-procedures";
  const TAB_RESCUE_READINESS = "rescue-readiness";

  const [activeTab, setActiveTab] = useState(TAB_SWMS);
  const [preStartToggle, setPreStartToggle] = useState(false);
  const [emergencyPlansToggle, setEmergencyPlansToggle] = useState(false);
  const [pendingEmergencyPlansToggle, setPendingEmergencyPlansToggle] = useState(null);
  const [emergencyPlansToggleConfirmVisible, setEmergencyPlansToggleConfirmVisible] = useState(false);
  const [emergencyPlansToggleSaving, setEmergencyPlansToggleSaving] = useState(false);
  const [emergencyToggleSaving, setEmergencyToggleSaving] = useState(false);
  const [pendingEmergencyToggle, setPendingEmergencyToggle] = useState(null);
  const [emergencyToggleConfirmVisible, setEmergencyToggleConfirmVisible] = useState(false);
  const [emergencyProceduresToggle, setEmergencyProceduresToggle] = useState(false);
  const [rescueReadinessToggle, setRescueReadinessToggle] = useState(false);
  const [readinessQuestions, setReadinessQuestions] = useState([]);
  const [readinessSavedQuestions, setReadinessSavedQuestions] = useState([]);
  const [readinessSavedToggle, setReadinessSavedToggle] = useState(false);
  const [readinessModal, setReadinessModal] = useState({ visible: false, mode: "add", question: null, index: null });
  const [readinessSaving, setReadinessSaving] = useState(false);
  const [readinessSaveError, setReadinessSaveError] = useState(null);
  const [readinessSavedHeading, setReadinessSavedHeading] = useState({});
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const [editHistoryVisible, setEditHistoryVisible] = useState(false);
  const [readinessHeading, setReadinessHeading] = useState("");
  const [swmsSaveRequest, setSwmsSaveRequest] = useState(0);
  const [plansSaveRequest, setPlansSaveRequest] = useState(0);
  const [proceduresSaveRequest, setProceduresSaveRequest] = useState(0);
  const [discardRequest, setDiscardRequest] = useState(0);
  const [swmsDirty, setSwmsDirty] = useState(false);
  const [plansDirty, setPlansDirty] = useState(false);
  const [proceduresDirty, setProceduresDirty] = useState(false);
  const [plansSaving, setPlansSaving] = useState(false);
  const [proceduresSaving, setProceduresSaving] = useState(false);
  const [swmsSaving, setSwmsSaving] = useState(false);
  const readinessSaveInFlight = useRef(false);
  const dispatch = useDispatch();

  const resolvedJobId = jobId 
    || (swms && swms.version && swms.version.job_id)
    || (swms && swms.task && (swms.task.job_id || swms.task.jobId));
  const resolvedSwmsId = (swms && swms.version && swms.version.swms_form_id) || (swms && swms.swms_form_id);
  const {
    formData,
    categories,
    methodItemTypeGroups,
    loadingData,
    refreshingAfterSave,
    checklistRequestInFlight,
    updatingPreStartToggle,
    pendingPreStartToggle,
    toggleConfirmVisible,
    loadFormData,
    abortRequests,
    closeToggleConfirmModal,
    openToggleConfirmModal,
    handleSaveQuestions: handleSaveQuestionsApi,
    handleConfirmPreStartToggle: handleConfirmPreStartToggleApi
  } = usePreStartChecklistApi({
    onClose,
    resolvedJobId,
    resolvedSwmsId,
    activeTab,
    tabPreStart: TAB_PRE_START,
    preStartToggle,
    onPreStartToggleChange: setPreStartToggle
  });
  const swmsData = formData && formData.swms_data ? formData.swms_data : EMPTY_SWMS_DATA;
  const swmsInitialValues = useMemo(() => serializeInitialValuesV3(swmsData), [swmsData]);
  const swmsBaselineValues = useMemo(
    () => JSON.parse(JSON.stringify(swmsInitialValues)),
    [swmsInitialValues]
  );
  const internalRevisionId = Number(swmsData && swmsData.internal_revision_id);
  const emergencyServerState = useMemo(() => {
    const swmsDocument = formData && formData.document ? formData.document : {};
    const plans = normalizeEmergencyServerState(swmsData && swmsData.emergency_plans, "plans");
    const procedures = normalizeEmergencyServerState(swmsData && swmsData.emergency_procedures, "procedures");
    const readiness = normalizeEmergencyServerState(swmsData && swmsData.emergency_readiness, "questions");
    const swmsPlans = normalizeEmergencyServerState(swmsDocument.emergency_plans, "plans");
    const swmsProcedures = normalizeEmergencyServerState(swmsDocument.emergency_procedures, "procedures");
    const swmsReadiness = normalizeEmergencyServerState(swmsDocument.rescue_readiness_questions, "questions");

    return {
      plans: { ...plans, exists: swmsPlans.exists, disabled: swmsPlans.disabled, toggle: swmsPlans.toggle },
      procedures: {
        ...procedures,
        exists: swmsProcedures.exists,
        disabled: swmsProcedures.disabled,
        toggle: swmsProcedures.toggle
      },
      readiness: {
        ...readiness,
        exists: swmsReadiness.exists,
        disabled: swmsReadiness.disabled,
        toggle: swmsReadiness.toggle
      }
    };
  }, [formData, swmsData]);
  const emergencyLoading = loadingData || refreshingAfterSave;
  const showEmergencyPlansTab = emergencyServerState.plans.exists;
  const showEmergencyProceduresTab = showEmergencyPlansTab && emergencyServerState.procedures.exists;
  const showEmergencyReadinessTab = showEmergencyPlansTab && emergencyServerState.readiness.exists;
  const emergencyPlanOptions = useMemo(() => getEmergencyPlanOptions(emergencyServerState.plans.data), [emergencyServerState.plans.data]);
  const hasReadinessQuestionChanges = JSON.stringify(readinessQuestions) !== JSON.stringify(readinessSavedQuestions)
    || rescueReadinessToggle !== readinessSavedToggle
    || JSON.stringify(readinessHeading) !== JSON.stringify(readinessSavedHeading);
  const readinessQuestionCount = readinessQuestions.length;

  const normalizedPreStartChecklist = useMemo(
    () => normalizePreStartChecklist(swmsData),
    [swmsData]
  );

  // const preStartHeadingContent = useMemo(
  //   () => resolvePreStartHeadingContent(swmsData),
  //   [swmsData]
  // );
  const {
    savedPreStartChecklist,
    workingPreStartChecklist,
    hasUnsavedQuestionChanges,
    questionCount: preStartQuestionCount,
    selectedQuestionByIndex,
    resetWorkingToSaved,
    replaceDraftWithServerChecklist,
    applyToggleServerChecklist,
    moveQuestionRow,
    onAfterDropQuestionRow,
    applyQuestionModalSubmit,
    updateHeadingContent,
    updateStaffSignoffHeadingContent
  } = usePreStartQuestionDraft({
    swmsId: resolvedSwmsId,
    initialChecklist: normalizedPreStartChecklist
  });
  const {
    questionModalVisible,
    questionModalMode,
    selectedQuestionIndex,
    closeQuestionModal,
    openAddQuestionModal,
    openEditQuestionModal
  } = usePreStartQuestionModalState();
  const methodOptions = useMemo(() => resolveMethodOptions(swmsData), [swmsData]);
  const selectedQuestion = selectedQuestionByIndex(selectedQuestionIndex);
  const preStartInteractionDisabled = checklistRequestInFlight || refreshingAfterSave;

  useEffect(() => {
    setPreStartToggle(normalizedPreStartChecklist.toggle === 1);
  }, [normalizedPreStartChecklist]);

  useEffect(() => {
    setEditHistoryVisible(false);
  }, [resolvedJobId, resolvedSwmsId, swmsData.currentVersion]);

  useEffect(() => {
    setEmergencyPlansToggle(emergencyServerState.plans.toggle);
    setEmergencyProceduresToggle(emergencyServerState.procedures.toggle);
    if (!hasReadinessQuestionChanges && !readinessSaving) {
      setRescueReadinessToggle(emergencyServerState.readiness.toggle);
    }
  }, [emergencyServerState]);

  useEffect(() => {
    const serverQuestions = Array.isArray(emergencyServerState.readiness.questions)
      ? emergencyServerState.readiness.questions
      : emergencyServerState.readiness.data;

    if (!Array.isArray(serverQuestions)) return;

    // The server response is the canonical baseline. Do not overwrite a working
    // draft when another tab refreshes the parent form, but do update the saved
    // baseline so Cancel always returns to the latest server state.
    const mappedQuestions = mapEmergencyReadinessQuestions(serverQuestions, emergencyPlanOptions);
    setReadinessSavedQuestions(mappedQuestions);
    setReadinessSavedToggle(emergencyServerState.readiness.toggle);
    if (!hasReadinessQuestionChanges && !readinessSaving) {
      setReadinessQuestions(mappedQuestions);
      setRescueReadinessToggle(emergencyServerState.readiness.toggle);
    }
    setReadinessHeading(emergencyServerState.readiness.headingContents || {});
    setReadinessSavedHeading(emergencyServerState.readiness.headingContents || {});
  }, [emergencyServerState.readiness.data, emergencyServerState.readiness.questions, emergencyPlanOptions]);

  const handleEmergencyPlansToggle = (checked) => {
    const included = Boolean(checked) && emergencyServerState.plans.data.length > 0;
    if (
      activeTab !== TAB_EMERGENCY_PLANS
      || emergencyServerState.plans.disabled
      || emergencyLoading
      || included === emergencyPlansToggle
    ) return;
    setPendingEmergencyPlansToggle(included);
    setEmergencyPlansToggleConfirmVisible(true);
  };

  const cancelEmergencyPlansToggle = () => {
    if (emergencyLoading) return;
    setPendingEmergencyPlansToggle(null);
    setEmergencyPlansToggleConfirmVisible(false);
  };

  const confirmEmergencyPlansToggle = () => {
    if (pendingEmergencyPlansToggle === null || emergencyLoading || emergencyPlansToggleSaving) return;

    setEmergencyPlansToggleSaving(true);
    const currentPlansToggle = emergencyServerState.plans.toggle ? 1 : 0;
    const nextPlansToggle = pendingEmergencyPlansToggle ? 1 : 0;
    const editContext = createSwmsEditContext({
      initialValues: { toggle: currentPlansToggle },
      submittedValues: { toggle: nextPlansToggle },
      internalRevisionId: Number.isInteger(Number(emergencyServerState.plans.internal_revision_id))
        ? Number(emergencyServerState.plans.internal_revision_id)
        : 0,
      documentName: "Emergency Plans",
      documentType: "emergency_plans",
      items: []
    });
    return dispatch(saveJobSwmsEmergencyPlans(
      resolvedJobId,
      resolvedSwmsId,
      emergencyServerState.plans.data,
      emergencyServerState.plans.version,
      nextPlansToggle,
      editContext
    )).then(() => {
      setEmergencyPlansToggle(pendingEmergencyPlansToggle);
      if (!pendingEmergencyPlansToggle) {
        setEmergencyProceduresToggle(false);
        setRescueReadinessToggle(false);
      }
      setPendingEmergencyPlansToggle(null);
      setEmergencyPlansToggleConfirmVisible(false);
      return loadFormData({ closeOnError: false, showBlockingLoader: false });
    }).catch((error) => {
      notification.error({
        message: Strings.error_title,
        description: typeof error === "string" ? error : Strings.generic_error,
        className: "ant-error"
      });
    }).finally(() => {
      setEmergencyPlansToggleSaving(false);
    });
  };

  const handleEmergencyProceduresToggle = (checked) => {
    if (
      activeTab !== TAB_EMERGENCY_PROCEDURES
      ||
      emergencyServerState.procedures.disabled
      || emergencyToggleSaving
      || emergencyLoading
      || emergencyServerState.procedures.data.length === 0
    ) return;

    setPendingEmergencyToggle({ type: TAB_EMERGENCY_PROCEDURES, enabled: Boolean(checked) });
    setEmergencyToggleConfirmVisible(true);
  };

  const handleRescueReadinessToggle = (checked) => {
    if (
      activeTab !== TAB_RESCUE_READINESS
      ||
      emergencyServerState.readiness.disabled
      || emergencyToggleSaving
      || emergencyLoading
      || emergencyServerState.readiness.data.length === 0
    ) return;

    setPendingEmergencyToggle({ type: TAB_RESCUE_READINESS, enabled: Boolean(checked) });
    setEmergencyToggleConfirmVisible(true);
  };

  const cancelEmergencyToggle = () => {
    if (emergencyToggleSaving) return;
    setPendingEmergencyToggle(null);
    setEmergencyToggleConfirmVisible(false);
  };

  const confirmEmergencyToggle = () => {
    if (!pendingEmergencyToggle || emergencyLoading || emergencyToggleSaving) return;

    const { type, enabled } = pendingEmergencyToggle;
    const currentReadiness = emergencyServerState.readiness;
    setEmergencyToggleSaving(true);
    const savePromise = type === TAB_EMERGENCY_PROCEDURES
      ? dispatch(saveJobSwmsEmergencyProcedures(
        resolvedJobId,
        resolvedSwmsId,
        emergencyServerState.procedures.data,
        emergencyServerState.procedures.version,
        enabled ? 1 : 0,
        createSwmsEditContext({
          initialValues: { toggle: emergencyServerState.procedures.toggle ? 1 : 0 },
          submittedValues: { toggle: enabled ? 1 : 0 },
          internalRevisionId: Number.isInteger(Number(emergencyServerState.procedures.internal_revision_id))
            ? Number(emergencyServerState.procedures.internal_revision_id)
            : 0,
          documentName: "Emergency Procedures",
          documentType: "emergency_procedures",
          items: []
        })
      ))
      : dispatch(saveJobSwmsEmergencyReadiness(
        resolvedJobId,
        resolvedSwmsId,
        {
          version: currentReadiness.version || 1,
          document: {
            heading_contents: currentReadiness.headingContents || {},
            questions: currentReadiness.data,
            toggle: enabled ? 1 : 0
          },
          // edit_context temporarily disabled for Emergency Readiness saves.
        }
      ));

    savePromise.then(() => {
      if (type === TAB_EMERGENCY_PROCEDURES) {
        setEmergencyProceduresToggle(enabled);
      } else {
        setRescueReadinessToggle(enabled);
      }
      setPendingEmergencyToggle(null);
      setEmergencyToggleConfirmVisible(false);
      return loadFormData({ closeOnError: false, showBlockingLoader: false });
    }).catch((error) => {
      notification.error({
        message: Strings.error_title,
        description: typeof error === "string" ? error : Strings.generic_error,
        className: "ant-error"
      });
    }).finally(() => {
      setEmergencyToggleSaving(false);
    });
  };

  const handleOpenAddQuestionModal = () => {
    openAddQuestionModal({ disabled: preStartInteractionDisabled });
  };

  const handleOpenEditQuestionModal = (_, index) => {
    openEditQuestionModal(index, { disabled: preStartInteractionDisabled });
  };

  const handleMoveQuestionRow = (fromIndex, toIndex) => {
    moveQuestionRow(fromIndex, toIndex, { disabled: preStartInteractionDisabled });
  };

  const handleAfterDropQuestionRow = (payload = {}) => {
    onAfterDropQuestionRow(payload, { disabled: preStartInteractionDisabled });
  };

  const handleSaveQuestions = () => {
    handleSaveQuestionsApi({
      savedPreStartChecklist,
      workingPreStartChecklist,
      hasUnsavedQuestionChanges,
      replaceDraftWithServerChecklist
    });
  };

  const handleCancelQuestions = () => {
    resetWorkingToSaved({ disabled: preStartInteractionDisabled });
  };

  const handleQuestionModalSubmit = (payload = {}) => {
    applyQuestionModalSubmit(payload);

    closeQuestionModal();
  };

  const openReadinessAdd = () => setReadinessModal({ visible: true, mode: "add", question: null, index: null });
  const openReadinessEdit = (question, index) => setReadinessModal({ visible: true, mode: "edit", question, index });
  const moveReadinessQuestion = (fromIndex, toIndex) => {
    setReadinessQuestions((current) => {
      const next = [...current];
      const moved = next.splice(fromIndex, 1)[0];
      next.splice(toIndex, 0, moved);
      return next;
    });
  };
  const saveReadinessQuestions = () => {
    if (readinessSaveInFlight.current || readinessSaving) return;

    const previousToggle = readinessSavedToggle;
    const currentReadiness = emergencyServerState.readiness;
    const currentQuestions = Array.isArray(readinessQuestions) ? readinessQuestions : [];
    const questions = currentQuestions.map((question, index) => ({
      ...question,
      display_order: index + 1
    }));
    const readinessPayload = {
      version: currentReadiness.version || 1,
      document: {
        heading_contents: readinessHeading || currentReadiness.headingContents || {},
        questions,
        toggle: rescueReadinessToggle ? 1 : 0
      },
      // edit_context temporarily disabled for Emergency Readiness saves.
    };

    readinessSaveInFlight.current = true;
    setReadinessSaving(true);
    setReadinessSaveError(null);
    dispatch(saveJobSwmsEmergencyReadiness(resolvedJobId, resolvedSwmsId, readinessPayload))
      .then((response) => {
        const saved = response && response.emergency_readiness
          ? response.emergency_readiness
          : readinessPayload.document;
        const canonicalQuestions = Array.isArray(saved.questions) ? saved.questions : questions;
        const canonicalToggle = saved.toggle === true || saved.toggle === 1 || saved.toggle === "1";
        setReadinessQuestions(canonicalQuestions);
        setReadinessSavedQuestions(canonicalQuestions);
        setReadinessSavedToggle(canonicalToggle);
        setReadinessSavedHeading(readinessPayload.document.heading_contents || {});
        setRescueReadinessToggle(canonicalToggle);
        notification.success({
          message: Strings.success_title,
          description: response && response.message ? response.message : "Emergency Readiness saved successfully.",
          className: "ant-success"
        });
        return loadFormData({ closeOnError: false, showBlockingLoader: false });
      })
      .catch((error) => {
        // Keep the edited questions, but roll the indicator back to the last
        // canonical value. A failed PUT must not look like a successful save.
        setRescueReadinessToggle(previousToggle);
        setReadinessSaveError(typeof error === "string" ? error : (error && error.message) || Strings.generic_error);
        notification.error({
          message: Strings.error_title,
          description: typeof error === "string" ? error : (error && error.message) || Strings.generic_error,
          className: "ant-error"
        });
      })
      .then(() => {
        readinessSaveInFlight.current = false;
        setReadinessSaving(false);
      });
  };
  const submitReadinessQuestion = (payload = {}) => {
    const { mode, selectedQuestion, question } = payload;

    if (!question) {
      return;
    }

    setReadinessQuestions((current) => mode === "edit"
      ? current.map((item) => `${item.id}` === `${selectedQuestion && selectedQuestion.id}` ? { ...item, ...question, id: item.id } : item)
      : [...current, { ...question, display_order: current.length + 1 }]);
    setReadinessModal({ visible: false, mode: "add", question: null, index: null });
  };

  const handleConfirmPreStartToggle = () => {
    handleConfirmPreStartToggleApi({
      savedPreStartChecklist,
      applyToggleServerChecklist
    });
  };

  const handleBackToSwms = () => {
    if (activeTab === TAB_PRE_START && hasUnsavedQuestionChanges) {
      const shouldDiscard = window.confirm("You have unsaved Pre-Start checklist changes. Discard changes and go back?");
      if (!shouldDiscard) {
        return;
      }

      resetWorkingToSaved();
    }

    onClose(null);
  };

  const handleSaveActiveTab = () => {
    if (activeTab === TAB_PRE_START) {
      handleSaveQuestions();
      return;
    }
    if (activeTab === TAB_RESCUE_READINESS) {
      saveReadinessQuestions();
      return;
    }
    if (activeTab === TAB_SWMS) {
      setSwmsSaveRequest((value) => value + 1);
    } else if (activeTab === TAB_EMERGENCY_PLANS) {
      setPlansSaveRequest((value) => value + 1);
    } else if (activeTab === TAB_EMERGENCY_PROCEDURES) {
      setProceduresSaveRequest((value) => value + 1);
    }
  };

  const activeTabDirty = activeTab === TAB_PRE_START
    ? hasUnsavedQuestionChanges
    : activeTab === TAB_RESCUE_READINESS
      ? hasReadinessQuestionChanges
      : activeTab === TAB_SWMS
        ? swmsDirty
          : activeTab === TAB_EMERGENCY_PLANS
            ? plansDirty
            : activeTab === TAB_EMERGENCY_PROCEDURES
              ? proceduresDirty
        : false;
  const activeTabSaving = checklistRequestInFlight
    || readinessSaving
    || refreshingAfterSave
    || (activeTab === TAB_SWMS && swmsSaving)
    || (activeTab === TAB_EMERGENCY_PLANS && plansSaving)
    || (activeTab === TAB_EMERGENCY_PROCEDURES && proceduresSaving);
  const handleDiscardActiveTab = () => {
    if (activeTab === TAB_PRE_START) {
      handleCancelQuestions();
    } else if (activeTab === TAB_RESCUE_READINESS) {
      setReadinessQuestions(readinessSavedQuestions);
      setRescueReadinessToggle(readinessSavedToggle);
      setReadinessHeading(readinessSavedHeading);
      setReadinessSaveError(null);
    } else {
      setDiscardRequest((value) => value + 1);
    }
  };

  useEffect(() => {
    loadFormData();

    return () => {
      abortRequests();
    };
  }, []);

  useEffect(() => {
    setActiveTab(TAB_SWMS);
  }, [resolvedSwmsId]);

  if (loadingData) {
    return (
      <div className="col-12 d-flex align-items-center justify-content-center p-5">
        <CircularProgress size={20} />
      </div>
    );
  }

  return (
    <div className={preStartStyles.FormTabsWrap}>
      {refreshingAfterSave && <LinearProgress />}

      <div className="px-2">
        <button
          className={`${commonStyles.BlueTextButton} px-2 ${commonStyles.PrimaryText}`}
          type="button"
          onClick={handleBackToSwms}
        >
          <KeyboardBackspace /> Back to SWMS
        </button>
      </div>

      <div
        className={preStartStyles.FormTabs}
        role="tablist"
        aria-label="SWMS form tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_SWMS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_SWMS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_SWMS)}
        >
          SWMS
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_PRE_START}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_PRE_START ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_PRE_START)}
        >
          <span className={preStartStyles.FormTabLabel}>Pre-Start</span>
          <span
            className={`${preStartStyles.SwmsTabToggle} ${preStartToggle ? preStartStyles.SwmsTabToggleActive : preStartStyles.SwmsTabToggleInactive}`.trim()}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              disabled={activeTab !== TAB_PRE_START || checklistRequestInFlight}
              checked={preStartToggle}
              onChange={openToggleConfirmModal}
              size="small"
            />
          </span>
          <span
            className={`${preStartStyles.FormTabMetaPill} ${activeTab === TAB_PRE_START ? "" : preStartStyles.FormTabMetaPillInactive}`.trim()}
          >
            {preStartQuestionCount} {preStartQuestionCount === 1 ? "question" : "questions"}
          </span>
        </button>
        {showEmergencyPlansTab ? <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_EMERGENCY_PLANS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_EMERGENCY_PLANS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_EMERGENCY_PLANS)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Plans</span>
          <span
            className={`${preStartStyles.SwmsTabToggle} ${emergencyPlansToggle ? preStartStyles.SwmsTabToggleActive : preStartStyles.SwmsTabToggleInactive}`.trim()}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={emergencyPlansToggle}
              disabled={activeTab !== TAB_EMERGENCY_PLANS || emergencyServerState.plans.disabled || emergencyLoading || emergencyServerState.plans.data.length === 0}
              onChange={handleEmergencyPlansToggle}
              size="small"
            />
          </span>
        </button> : null}
        {showEmergencyProceduresTab ? <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_EMERGENCY_PROCEDURES}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_EMERGENCY_PROCEDURES ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_EMERGENCY_PROCEDURES)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Procedures</span>
          <span
            className={`${preStartStyles.SwmsTabToggle} ${emergencyProceduresToggle ? preStartStyles.SwmsTabToggleActive : preStartStyles.SwmsTabToggleInactive}`.trim()}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={emergencyProceduresToggle}
              disabled={activeTab !== TAB_EMERGENCY_PROCEDURES || emergencyServerState.procedures.disabled || emergencyToggleSaving || emergencyLoading || emergencyServerState.procedures.data.length === 0}
              onChange={handleEmergencyProceduresToggle}
              size="small"
            />
          </span>
        </button> : null}
        {showEmergencyReadinessTab ? <button
          type="button"
          role="tab"
          aria-selected={activeTab === TAB_RESCUE_READINESS}
          className={`${preStartStyles.FormTabButton} ${activeTab === TAB_RESCUE_READINESS ? preStartStyles.FormTabButtonActive : preStartStyles.FormTabButtonInactive}`.trim()}
          onClick={() => setActiveTab(TAB_RESCUE_READINESS)}
        >
          <span className={preStartStyles.FormTabLabel}>Emergency Readiness</span>
          <span
            className={`${preStartStyles.SwmsTabToggle} ${rescueReadinessToggle ? preStartStyles.SwmsTabToggleActive : preStartStyles.SwmsTabToggleInactive}`.trim()}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Switch
              className="cs-switch-v2"
              checked={rescueReadinessToggle}
              disabled={activeTab !== TAB_RESCUE_READINESS || emergencyServerState.readiness.disabled || emergencyToggleSaving || emergencyLoading || emergencyServerState.readiness.data.length === 0}
              onChange={handleRescueReadinessToggle}
              size="small"
            />
          </span>
          <span
            className={`${preStartStyles.FormTabMetaPill} ${activeTab === TAB_RESCUE_READINESS ? "" : preStartStyles.FormTabMetaPillInactive}`.trim()}
          >
            {readinessQuestionCount} {readinessQuestionCount === 1 ? "question" : "questions"}
          </span>
        </button> : null}
      </div>

      <div
        className={activeTab === TAB_SWMS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}>
        <div style={{ position: "relative" }}>
          <JobSwmsFormV3Shell
            onClose={onClose}
            swmsData={swmsData}
            initialValues={swmsInitialValues}
            baselineValues={swmsBaselineValues}
            internalRevisionId={Number.isInteger(internalRevisionId) ? internalRevisionId : 0}
            currentVersion={swmsData.currentVersion}
            jobId={resolvedJobId}
            approvalSubmissionHistory={formData && formData.approvalSubmissionHistory ? formData.approvalSubmissionHistory : []}
            swmsTitle={formData && formData.title ? formData.title : ""}
            swmsNumber={formData && formData.swmsNumber ? formData.swmsNumber : ""}
            swmsStatus={formData && formData.currentStatus ? formData.currentStatus : null}
            swmsId={resolvedSwmsId}
            refreshForm={loadFormData}
            saveRequest={swmsSaveRequest}
            discardRequest={activeTab === TAB_SWMS ? discardRequest : 0}
            onSavingChange={setSwmsSaving}
            onDirtyChange={setSwmsDirty}
          />
          {refreshingAfterSave ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.82)"
              }}
            />
          ) : null}
        </div>
      </div>

      <EmergencyPlansToggleConfirmModal
        visible={emergencyPlansToggleConfirmVisible}
        targetEnabled={pendingEmergencyPlansToggle === true}
        loading={emergencyPlansToggleSaving}
        onCancel={cancelEmergencyPlansToggle}
        onConfirm={confirmEmergencyPlansToggle}
      />
      <EmergencyPlansToggleConfirmModal
        visible={emergencyToggleConfirmVisible}
        targetEnabled={pendingEmergencyToggle ? pendingEmergencyToggle.enabled : false}
        documentLabel={pendingEmergencyToggle && pendingEmergencyToggle.type === TAB_RESCUE_READINESS
          ? "Emergency Readiness"
          : "Emergency Procedures"}
        loading={emergencyToggleSaving}
        onCancel={cancelEmergencyToggle}
        onConfirm={confirmEmergencyToggle}
      />

      <div
        className={activeTab === TAB_PRE_START ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}>
        <div style={{ position: "relative" }}>
          <JobSwmsV3PreStartContainer
            checklist={workingPreStartChecklist}
            initialValues={{
              prestart_checklist_heading: workingPreStartChecklist
                && workingPreStartChecklist.data
                && typeof workingPreStartChecklist.data.prestart_checklist_heading === "string"
                ? workingPreStartChecklist.data.prestart_checklist_heading
                : "",
              prestart_staff_signoff: workingPreStartChecklist
                && workingPreStartChecklist.data
                && typeof workingPreStartChecklist.data.prestart_staff_signoff === "string"
                ? workingPreStartChecklist.data.prestart_staff_signoff
                : ""
            }}
            onOpenAddQuestion={handleOpenAddQuestionModal}
            onOpenEditQuestion={handleOpenEditQuestionModal}
            moveQuestionRow={handleMoveQuestionRow}
            onAfterDropQuestionRow={handleAfterDropQuestionRow}
            onCancelQuestions={handleCancelQuestions}
            onSaveQuestions={handleSaveQuestions}
            onHeadingContentChange={updateHeadingContent}
            onStaffSignoffHeadingContentChange={updateStaffSignoffHeadingContent}
            hasUnsavedQuestionChanges={hasUnsavedQuestionChanges}
            questionInteractionDisabled={preStartInteractionDisabled}
            requestInFlight={preStartInteractionDisabled}
          />
          {refreshingAfterSave ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                background: "rgba(255, 255, 255, 0.82)"
              }}
            />
          ) : null}
        </div>
      </div>

      {showEmergencyPlansTab ? <div
        className={activeTab === TAB_EMERGENCY_PLANS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <div style={{ position: "relative" }}>
          <JobSwmsV3EmergencyPlansContainer
              serverState={{ ...emergencyServerState.plans, loading: loadingData }}
              version={emergencyServerState.plans.version}
              included={emergencyPlansToggle}
              toggle={emergencyPlansToggle}
              initialValues={{ plans: emergencyServerState.plans.data.map((plan) => ({
                ...normalizeEmergencyPlans([plan])[0],
                category: plan.category
                  ? {
                    label: plan.category.category_name || plan.category.name,
                    value: plan.category.id
                  }
                  : null,
                toggle: plan.toggle === undefined ? 1 : Number(plan.toggle)
              })) }}
              categories={categories}
            swmsStatus={formData && formData.currentStatus ? formData.currentStatus : ""}
            swmsData={swmsData}
            methodItemTypeGroups={methodItemTypeGroups}
            methodItemTypesLoading={emergencyLoading}
            jobId={resolvedJobId}
            swmsId={resolvedSwmsId}
            onClose={onClose}
            refreshForm={loadFormData}
            onRetry={() => loadFormData()}
            saveRequest={plansSaveRequest}
            discardRequest={activeTab === TAB_EMERGENCY_PLANS ? discardRequest : 0}
            onDirtyChange={setPlansDirty}
            onSavingChange={setPlansSaving}
            />
          {refreshingAfterSave ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                background: "rgba(255, 255, 255, 0.82)"
              }}
            />
          ) : null}
        </div>
      </div> : null}

      {showEmergencyProceduresTab ? <div
        className={activeTab === TAB_EMERGENCY_PROCEDURES ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <div style={{ position: "relative" }}>
          <JobSwmsV3EmergencyProceduresContainer
              serverState={{ ...emergencyServerState.procedures, loading: loadingData }}
              plans={emergencyServerState.plans.data}
              swmsData={swmsData}
            jobId={resolvedJobId}
            swmsId={resolvedSwmsId}
            included={emergencyProceduresToggle}
            toggle={emergencyProceduresToggle}
            initialValues={{
              procedures: normalizeEmergencyProcedures(emergencyServerState.procedures.data)
            }}
            onClose={onClose}
            refreshForm={loadFormData}
          onRetry={() => loadFormData()}
          saveRequest={proceduresSaveRequest}
          discardRequest={activeTab === TAB_EMERGENCY_PROCEDURES ? discardRequest : 0}
          onDirtyChange={setProceduresDirty}
          onSavingChange={setProceduresSaving}
          />
          {refreshingAfterSave ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                background: "rgba(255, 255, 255, 0.82)"
              }}
            />
          ) : null}
        </div>
      </div> : null}

      {showEmergencyReadinessTab ? <div
        className={activeTab === TAB_RESCUE_READINESS ? preStartStyles.FormTabPaneActive : preStartStyles.FormTabPaneHidden}
      >
        <div style={{ position: "relative" }}>
          <JobSwmsV3RescueReadinessContainer
            serverState={{ ...emergencyServerState.readiness, data: readinessQuestions, questions: readinessQuestions, loading: loadingData, error: readinessSaveError || emergencyServerState.readiness.error }}
            included={rescueReadinessToggle}
            toggle={rescueReadinessToggle}
            emergencyPlanOptions={emergencyPlanOptions}
            onOpenAddQuestion={openReadinessAdd}
            initialValues={{
              readiness_checklist_heading: readinessHeading.rescue_readiness_checklist_heading || "",
              readiness_staff_signoff: readinessHeading.rescue_readiness_staff_signoff || ""
            }}
            onHeadingContentChange={(heading) => setReadinessHeading((current) => ({
              ...current,
              rescue_readiness_checklist_heading: heading
            }))}
            onStaffSignoffHeadingContentChange={(heading) => setReadinessHeading((current) => ({
              ...current,
              rescue_readiness_staff_signoff: heading
            }))}
            onOpenEditQuestion={openReadinessEdit}
            moveQuestionRow={moveReadinessQuestion}
            onCancelQuestions={() => {
            setReadinessQuestions(readinessSavedQuestions);
            setRescueReadinessToggle(readinessSavedToggle);
            setReadinessSaveError(null);
          }}
            hasUnsavedQuestionChanges={hasReadinessQuestionChanges}
            onSaveQuestions={saveReadinessQuestions}
            questionInteractionDisabled={emergencyLoading || readinessSaving}
            requestInFlight={readinessSaving}
          onRetry={() => loadFormData()}
          />
          {refreshingAfterSave ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                background: "rgba(255, 255, 255, 0.82)"
              }}
            />
          ) : null}
        </div>
      </div> : null}

      <JobSwmsV3AddEditRescueReadinessQuestionModal
        visible={readinessModal.visible}
        mode={readinessModal.mode}
        selectedQuestion={readinessModal.question}
        emergencyPlanOptions={emergencyPlanOptions}
        onCancel={() => setReadinessModal({ visible: false, mode: "add", question: null, index: null })}
        onSubmit={submitReadinessQuestion}
      />

      <PreStartToggleConfirmModal
        visible={toggleConfirmVisible}
        targetEnabled={Boolean(pendingPreStartToggle)}
        loading={updatingPreStartToggle}
        onCancel={closeToggleConfirmModal}
        onConfirm={handleConfirmPreStartToggle}
      />

      <JobSwmsV3AddEditPreStartQuestionModal
        visible={questionModalVisible}
        mode={questionModalMode}
        selectedQuestion={selectedQuestion}
        methodOptions={methodOptions}
        onCancel={closeQuestionModal}
        onSubmit={handleQuestionModalSubmit}
      />

      <JobSwmsFormV3SaveBar
        onSave={handleSaveActiveTab}
        onDiscard={handleDiscardActiveTab}
        onCancel={handleBackToSwms}
        onPreview={() => {
          if (onPreview && resolvedSwmsId) {
            onPreview({ swms_form_id: resolvedSwmsId });
          }
        }}
        onVersionApprovalHistory={() => setVersionHistoryVisible(true)}
        onEditHistory={() => setEditHistoryVisible(true)}
        editHistoryDisabled={!resolvedJobId || !resolvedSwmsId || !swmsData.currentVersion}
        isDirty={activeTabDirty}
        isSaving={activeTabSaving}
      />
      <VersionApprovalHistoryModal
        visible={versionHistoryVisible}
        onClose={() => setVersionHistoryVisible(false)}
        approvalSubmissionHistory={formData && formData.approvalSubmissionHistory ? formData.approvalSubmissionHistory : []}
        swmsData={swmsData}
        loading={loadingData}
      />
      <EditHistoryModal
        visible={editHistoryVisible}
        onClose={() => setEditHistoryVisible(false)}
        jobId={resolvedJobId}
        swmsId={resolvedSwmsId}
        version={swmsData.currentVersion}
      />
    </div>
  );
}
